import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Pagination from "react-js-pagination";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { IconButton } from "@mui/material";
import { IoSearch } from "react-icons/io5";
import { Input, TextArea } from "../../components/Form";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import useButtonLoader from "../../hooks/UseButtonLoader";
import { toastMessage } from "../../utils/toastMessage";
import { SUPPORT_QUERY_LANGUAGES } from "../../utils/appLanguages";
import {
  fetchQueryMastersApi,
  saveQueryMasterTranslationApi,
} from "../../services/supportManagement";
import "./SupportQueryMasters.css";

const emptyTranslation = {
  mainCategory: "",
  subCategory: "",
  question: "",
  answer: "",
};

const SupportQueryMasters = () => {
  const [searchParam, setSearchParam] = useSearchParams();
  const activePage = +searchParam.get("page") || 1;
  const [search, setSearch] = useState(searchParam.get("search") || "");
  const debounceValue = useDebounce(search || "", 300);
  const [userType, setUserType] = useState(searchParam.get("userType") || "user");
  const initialLang = searchParam.get("targetLang") || "hi";
  const [targetLang, setTargetLang] = useState(
    SUPPORT_QUERY_LANGUAGES.some((item) => item.code === initialLang) ? initialLang : "hi"
  );
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [translation, setTranslation] = useState(emptyTranslation);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [buttonRef, buttonLoader] = useButtonLoader("Save");

  const languageLabel =
    SUPPORT_QUERY_LANGUAGES.find((item) => item.code === targetLang)?.label || "Hindi";

  const loadList = async () => {
    try {
      showLoader();
      const sendData = {
        page: String(activePage),
        limit: "20",
        sourceLang: "en",
        targetLang,
        userType,
      };
      if (debounceValue) sendData.search = debounceValue;
      const { data } = await fetchQueryMastersApi(sendData);
      if (data.code === 200) {
        const nextList = data?.result?.list || [];
        setList(nextList);
        setTotal(data?.result?.total || 0);
        const stillSelected = nextList.find((item) => item._id === selected?._id);
        if (stillSelected) {
          selectDocument(stillSelected);
        } else if (nextList[0]) {
          selectDocument(nextList[0]);
        } else {
          setSelected(null);
          setTranslation(emptyTranslation);
        }
      } else {
        setList([]);
        setTotal(0);
        toastMessage(data.message, "error", "queryMasters");
      }
    } catch (error) {
      console.log(error);
      toastMessage("Unable to load support query masters", "error", "queryMasters");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, debounceValue, userType, targetLang]);

  const updateParam = (key, value) => {
    if (value) searchParam.set(key, value);
    else searchParam.delete(key);
    searchParam.delete("page");
    setSearchParam(searchParam);
  };

  const selectDocument = (item) => {
    setSelected(item);
    const existing = targetLang === "en" ? item : item?.translation || {};
    setTranslation({
      mainCategory: existing.mainCategory || "",
      subCategory: existing.subCategory || "",
      question: existing.question || "",
      answer: existing.answer || "",
    });
  };

  const handleTranslationChange = (event) => {
    const { name, value } = event.target;
    setTranslation((current) => ({ ...current, [name]: value }));
  };

  const saveTranslation = async () => {
    if (!selected?._id) return;
    if (!translation.mainCategory || !translation.subCategory || !translation.question || !translation.answer) {
      toastMessage("Please fill category, subcategory, question and answer", "error", "queryMasters");
      return;
    }
    try {
      buttonLoader(true);
      const { data } = await saveQueryMasterTranslationApi({
        sourceId: selected._id,
        lang: targetLang,
        ...translation,
      });
      if (data.code === 200) {
        toastMessage(data.message || "Translation saved", "success", "queryMasters");
        loadList();
      } else {
        toastMessage(data.message, "error", "queryMasters");
      }
    } catch (error) {
      toastMessage(error?.response?.data?.message || "Unable to save translation", "error", "queryMasters");
    } finally {
      buttonLoader(false);
    }
  };

  const savedOnPage = list.filter((item) => item.translation || targetLang === "en").length;

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="query-masters">
            <div className="TitleBox query-masters-header">
              <div className="query-masters-title">
                <Link to="/support-management">
                  <IconButton>
                    <ArrowBackIosNewIcon className="back_button" />
                  </IconButton>
                </Link>
                <h4 className="Title">Support Query Masters</h4>
              </div>
              <div className="query-masters-lang">
                <label htmlFor="support-query-language">Language</label>
                <select
                  id="support-query-language"
                  className="form-control"
                  value={targetLang}
                  onChange={(event) => {
                    setTargetLang(event.target.value);
                    updateParam("targetLang", event.target.value);
                  }}
                >
                  {SUPPORT_QUERY_LANGUAGES.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="FilterBox FilterArea query-masters-filters">
              <div className="form-group search_lg_size userSearch">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search by category, question, or query code"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <span className="Search">
                  <IoSearch />
                </span>
              </div>
              <div className="query-masters-pills">
                <button
                  type="button"
                  className={userType === "user" ? "active" : ""}
                  onClick={() => {
                    setUserType("user");
                    updateParam("userType", "user");
                  }}
                >
                  User
                </button>
                <button
                  type="button"
                  className={userType === "cons" ? "active" : ""}
                  onClick={() => {
                    setUserType("cons");
                    updateParam("userType", "cons");
                  }}
                >
                  Consultant
                </button>
              </div>
            </div>

            <div className="query-masters-layout">
              <div className="query-masters-panel">
                <div className="query-masters-panel-head">
                  <h5>English documents</h5>
                  <span className="query-masters-count">
                    {targetLang === "en" ? `${total} total` : `${savedOnPage} saved on this page · ${total} total`}
                  </span>
                </div>
                {list.length ? (
                  <div className="query-masters-list">
                    {list.map((item) => {
                      const isActive = selected?._id === item._id;
                      const isSaved = targetLang === "en" || Boolean(item.translation);
                      return (
                        <button
                          type="button"
                          key={item._id}
                          className={`query-masters-card ${isActive ? "active" : ""}`}
                          onClick={() => selectDocument(item)}
                        >
                          <div className="query-masters-card-top">
                            <span className="query-masters-code">{item.queryCode}</span>
                            <span className={`query-masters-badge ${isSaved ? "saved" : "pending"}`}>
                              {isSaved ? "Saved" : "Missing"}
                            </span>
                          </div>
                          <strong>{item.mainCategory}</strong>
                          <small>{item.subCategory}</small>
                          <p>{item.question}</p>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="query-masters-empty">No English support queries found.</div>
                )}
                {total > 20 && (
                  <div className="PaginationBox" style={{ marginTop: 12 }}>
                    <Pagination
                      activePage={activePage}
                      itemsCountPerPage={20}
                      totalItemsCount={total}
                      pageRangeDisplayed={5}
                      onChange={(page) => {
                        searchParam.set("page", page);
                        setSearchParam(searchParam);
                      }}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                )}
              </div>

              <div className="query-masters-panel query-masters-editor">
                <div className="query-masters-panel-head">
                  <h5>{languageLabel}</h5>
                  <span className="query-masters-count">
                    {selected?.queryCode || "Select a document"}
                  </span>
                </div>
                {selected ? (
                  <>
                    <div className="query-masters-field">
                      <div className="query-masters-source">English: {selected.mainCategory}</div>
                      <Input
                        type="text"
                        name="mainCategory"
                        value={translation.mainCategory}
                        onChange={handleTranslationChange}
                        placeholder={`${languageLabel} main category`}
                        className="form-control"
                        label="Main category"
                        required
                      />
                    </div>
                    <div className="query-masters-field">
                      <div className="query-masters-source">English: {selected.subCategory}</div>
                      <Input
                        type="text"
                        name="subCategory"
                        value={translation.subCategory}
                        onChange={handleTranslationChange}
                        placeholder={`${languageLabel} sub category`}
                        className="form-control"
                        label="Sub category"
                        required
                      />
                    </div>
                    <div className="query-masters-field">
                      <div className="query-masters-source">English: {selected.question}</div>
                      <TextArea
                        name="question"
                        value={translation.question}
                        onChange={handleTranslationChange}
                        placeholder={`${languageLabel} question`}
                        className="form-control"
                        label="Question"
                        required
                        rows={3}
                      />
                    </div>
                    <div className="query-masters-field">
                      <div className="query-masters-source">English: {selected.answer}</div>
                      <TextArea
                        name="answer"
                        value={translation.answer}
                        onChange={handleTranslationChange}
                        placeholder={`${languageLabel} answer`}
                        className="form-control"
                        label="Answer"
                        required
                        rows={5}
                      />
                    </div>
                    <div className="query-masters-actions">
                      <button className="btn_size" onClick={saveTranslation} ref={buttonRef}>
                        Save {languageLabel}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="query-masters-empty">
                    Select an English document on the left, then enter the {languageLabel} text.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportQueryMasters;
